import TTUploader, { type Action } from 'tt-uploader';
import { TT_UPLOADER_APPID } from '@/config/env';
import { generateOssKeys } from '@/api/modules/resource/system';
import { useStsStore, type StsToken } from '@/stores/stsStore';
import { useUserStore } from '@/stores/userStore';
import { computeFileMd5 } from '@/pages/AssetCenter/MaterialLibrary/components/MaterialUploadSheet/computeFileMd5';
import {
  getCallbackArgs,
  isStsTokenExpiredError,
} from '@/pages/AssetCenter/MaterialLibrary/components/MaterialUploadSheet/utils';
import type { UploadTaskFile } from './types';

/** 文件夹场景同时直传的文件数上限 */
const MAX_CONCURRENT_UPLOADS = 3;
/** generateOssKeys 单次申请上限 */
const OSS_KEYS_BATCH_LIMIT = 300;

/** 内核对外暴露的文件状态变更补丁 */
export type UploadFilePatch = Partial<
  Pick<
    UploadTaskFile,
    | 'status'
    | 'progress'
    | 'errorMessage'
    | 'fileKey'
    | 'info'
    | 'computedMd5'
    | 'md5Computing'
    | 'retryable'
    | 'generatedFileName'
    | 'itemId'
    | 'targetFolderId'
    | 'reported'
  >
>;

interface UploadClientCallbacks {
  /** 单文件状态变化（进度 / 成功 / 失败） */
  onFilePatch: (uid: string, patch: UploadFilePatch) => void;
  /** 单个文件 settle（成功或失败） */
  onFileSettled: (uid: string) => void;
}

interface EnqueueParams {
  files: UploadTaskFile[];
  callbacks: UploadClientCallbacks;
}

/** 一个队列项在内核内的运行时记录 */
interface QueueEntry {
  uid: string;
  file: File;
  uploadType: 'image' | 'video' | 'audio';
  fileBizType: UploadTaskFile['fileBizType'];
  /** 首次申请的 OSS 路径，重试必须复用 */
  generatedFileName?: string;
  /** 当前 TTUploader 任务 key */
  fileKey?: string;
  /** 是否已结束（成功/失败/取消），用于避免重复 settle */
  settled: boolean;
  /** MD5 计算完成后的结果 */
  computedMd5?: string;
  /** MD5 是否仍在计算中 */
  md5Computing: boolean;
  /** MD5 计算中断控制器 */
  md5Controller?: AbortController;
  callbacks: UploadClientCallbacks;
  /** 已因凭证过期自动重试过一次 */
  hasRetriedExpiredToken: boolean;
}

/**
 * 全局唯一的上传内核。
 *
 * 关键约束（与设计文档 §3.11 对应）：
 * 1. 单例：TTUploader 实例按 userId 缓存，userId 变化时 reset()。
 * 2. **禁止 removeAllListeners**：单例下会误杀同实例的其他任务；
 *    每次 addFile 后只注册自己的 handler，并用 removeListener(event, ownHandler) 精确摘除。
 * 3. handler 必须按 fileKey 过滤（SDK 事件是实例级广播）。
 * 4. disposed 语义由「组件卸载」改为「任务取消 / 用户登出」。
 * 5. 不调用 disposeMd5Workers()（worker 池本身是全局单例，天然适配）。
 */
class UploadClient {
  private uploader: TTUploader | null = null;
  private uploaderUserId: string | null = null;
  /** uid -> 运行时记录 */
  private entries = new Map<string, QueueEntry>();
  /** 等待消费的 uid 队列（文件夹场景用 worker 池并发消费） */
  private pending: string[] = [];
  private activeWorkers = 0;

  /**
   * 创建（或复用）TTUploader 实例。
   * userId 变化时重建：先取消所有活动任务，再销毁旧实例。
   */
  private ensureUploader(userId: string): TTUploader {
    if (this.uploader && this.uploaderUserId === userId) return this.uploader;
    if (this.uploader) {
      this.cancelAllActive();
    }
    this.uploader = new TTUploader({
      userId: userId || '',
      appId: +TT_UPLOADER_APPID,
      region: 'ap-southeast-1',
      videoConfig: {
        spaceName: 'lightx-for-ads',
      },
      getSliceFunc: (fileSize) => {
        if (fileSize < 100 * 1024 * 1024) return 2 * 1024 * 1024;
        if (fileSize < 500 * 1024 * 1024) return 5 * 1024 * 1024;
        return 10 * 1024 * 1024;
      },
    });
    this.uploaderUserId = userId;
    return this.uploader;
  }

  /** 取消所有活动中的 TTUploader 任务（reset / 登出时用） */
  private cancelAllActive() {
    if (!this.uploader) return;
    for (const entry of this.entries.values()) {
      if (entry.fileKey) {
        try {
          this.uploader.cancel(entry.fileKey);
          this.uploader.removeFile(entry.fileKey);
        } catch {
          /* 实例可能已失效，忽略 */
        }
      }
    }
  }

  /**
   * 入队并开始上传。
   * 素材场景（文件数少）与文件夹场景（可能上百）共用同一入口，
   * 由内部 worker 池限制并发数。
   */
  enqueue({ files, callbacks }: EnqueueParams) {
    const userId = useUserStore.getState().userInfo?.userId || '';
    const uploader = this.ensureUploader(userId);

    for (const file of files) {
      this.entries.set(file.uid, {
        uid: file.uid,
        file: file.file,
        uploadType: file.uploadType,
        fileBizType: file.fileBizType,
        generatedFileName: file.generatedFileName,
        fileKey: undefined,
        settled: false,
        computedMd5: file.computedMd5,
        md5Computing: file.md5Computing ?? false,
        callbacks,
        hasRetriedExpiredToken: false,
      });
      this.pending.push(file.uid);
    }

    // 启动 worker 池：与改造前一致，最多 3 个文件同时直传
    this.pumpWorkers(uploader);
  }

  /** 消费队列：每个 worker 等待当前文件 settle 后再领下一个 */
  private pumpWorkers(uploader: TTUploader) {
    const limit = MAX_CONCURRENT_UPLOADS;
    while (this.activeWorkers < limit && this.pending.length > 0) {
      const uid = this.pending.shift()!;
      const entry = this.entries.get(uid);
      if (!entry || entry.settled) continue;
      this.activeWorkers += 1;
      void this.runEntry(entry, uploader).finally(() => {
        this.activeWorkers -= 1;
        this.pumpWorkers(uploader);
      });
    }
  }

  /**
   * 执行一个文件的完整上传流程：
   * 申请 OSS 路径 → 并行算 MD5 → 取 STS → addFile → start → 等 settle。
   */
  private async runEntry(entry: QueueEntry, uploader: TTUploader): Promise<void> {
    // 用户上下文校验（与改造前一致，失败直接记错误）
    const userInfo = useUserStore.getState().userInfo;
    if (!userInfo || !userInfo.team) {
      this.patchEntry(entry, { status: 'error', errorMessage: '用户信息获取异常，请联系管理员' });
      this.settleEntry(entry);
      return;
    }

    // 复用重试时已申请的 OSS 路径，避免续传语义错乱
    if (!entry.generatedFileName) {
      try {
        const response = await generateOssKeys(
          entry.fileBizType,
          [getExtSafe(entry.file.name)],
          OSS_KEYS_BATCH_LIMIT
        );
        const fileName = response.data?.[0];
        if (!fileName) throw new Error('生成上传路径失败');
        entry.generatedFileName = fileName;
        this.patchEntry(entry, { generatedFileName: fileName });
      } catch {
        this.patchEntry(entry, { status: 'error', errorMessage: '生成上传路径失败，请稍后重试' });
        this.settleEntry(entry);
        return;
      }
    }

    // 并行计算 MD5（不阻塞上传；失败不阻塞提交流程）
    if (!entry.computedMd5 && !entry.md5Computing) {
      entry.md5Computing = true;
      this.patchEntry(entry, { md5Computing: true });
      const controller = new AbortController();
      entry.md5Controller = controller;
      void computeFileMd5(entry.file, controller.signal)
        .then((md5) => {
          entry.computedMd5 = md5;
          this.patchEntry(entry, { md5Computing: false, computedMd5: md5 });
        })
        .catch(() => {
          this.patchEntry(entry, { md5Computing: false });
        })
        .finally(() => {
          entry.md5Computing = false;
          entry.md5Controller = undefined;
        });
    }

    let stsToken: StsToken;
    try {
      stsToken = await useStsStore.getState().getStsToken();
    } catch {
      this.patchEntry(entry, { status: 'error', errorMessage: '获取上传凭证失败，请稍后重试' });
      this.settleEntry(entry);
      return;
    }

    if (entry.settled) return;

    await new Promise<void>((resolve) => {
      this.startAttempt(entry, uploader, stsToken, () => resolve());
    });
  }

  /**
   * 创建并启动单次 TTUploader 任务。
   * 事件 handler 注册在实例上，必须按 fileKey 过滤。
   */
  private startAttempt(
    entry: QueueEntry,
    uploader: TTUploader,
    stsToken: StsToken,
    onSettled: () => void
  ) {
    const userInfo = useUserStore.getState().userInfo!;
    const cbArgs = getCallbackArgs({
      clientId: userInfo.clientId,
      userId: userInfo.userId,
      orgId: (userInfo.team?.companyOrgId ?? '') + '',
      originalName: entry.file.name,
      fileBizType: entry.fileBizType as Parameters<typeof getCallbackArgs>[0]['fileBizType'],
    });

    let fileKey: string;
    try {
      fileKey = uploader.addFile({
        file: entry.file,
        stsToken,
        type: entry.uploadType === 'audio' ? 'media' : entry.uploadType,
        callbackArgs: cbArgs,
        fileName: entry.generatedFileName,
        processAction:
          entry.uploadType === 'image' || entry.uploadType === 'audio'
            ? ([
                {
                  Name: 'AddOptionInfo',
                  input: { RecordType: 2, Category: entry.uploadType },
                },
              ] as unknown as Action[])
            : undefined,
      });
    } catch (error) {
      const message = (error as { message?: string })?.message || '未知错误';
      this.patchEntry(entry, { status: 'error', errorMessage: message });
      this.settleEntry(entry);
      onSettled();
      return;
    }
    entry.fileKey = fileKey;
    this.patchEntry(entry, { fileKey, status: 'uploading', progress: 0, errorMessage: undefined });

    /** 精确摘除本次注册的三个监听，绝不用 removeAllListeners */
    const cleanup = () => {
      uploader.removeListener('progress', handleProgress);
      uploader.removeListener('complete', handleComplete);
      uploader.removeListener('error', handleError);
    };

    const handleProgress = (data: unknown) => {
      const progressData = data as { key: string; percent: number };
      if (progressData.key !== fileKey) return;
      this.patchEntry(entry, { progress: Math.round(progressData.percent) });
    };

    const handleComplete = (data: unknown) => {
      const completeData = data as {
        key: string;
        uploadResult: {
          SourceInfo: {
            FileName: string;
            Md5: string;
            Width?: number;
            Height?: string;
            Duration?: number;
          };
        };
      };
      if (completeData.key !== fileKey) return;
      cleanup();
      const source = completeData.uploadResult.SourceInfo;
      const info: UploadTaskFile['info'] = {
        ossKey: source.FileName,
        md5: entry.computedMd5 || source.Md5 || undefined,
        width: source.Width ? String(source.Width) : undefined,
        height: source.Height ? String(source.Height) : undefined,
        duration: source.Duration,
      };
      this.patchEntry(entry, { status: 'success', progress: 100, info });
      this.settleEntry(entry);
      onSettled();
    };

    const handleError = async (data: unknown) => {
      const errorData = data as { key: string; extra?: { errorCode?: number; message?: string } };
      if (errorData.key !== fileKey) return;
      cleanup();

      // 凭证过期：换新凭证自动重试一次（保留改造前的限次逻辑）
      if (!entry.hasRetriedExpiredToken && isStsTokenExpiredError(errorData.extra?.errorCode)) {
        entry.hasRetriedExpiredToken = true;
        try {
          const refreshed = await useStsStore.getState().refreshStsToken(stsToken);
          uploader.removeFile(fileKey);
          entry.fileKey = undefined;
          if (entry.settled) {
            onSettled();
            return;
          }
          this.patchEntry(entry, { status: 'uploading', progress: 0, errorMessage: undefined });
          this.startAttempt(entry, uploader, refreshed, onSettled);
          return;
        } catch (error) {
          this.patchEntry(entry, {
            status: 'error',
            errorMessage: (error as { message?: string })?.message || '刷新上传凭证失败',
          });
          this.settleEntry(entry);
          onSettled();
          return;
        }
      }

      this.patchEntry(entry, {
        status: 'error',
        errorMessage: errorData.extra?.message || '未知错误',
      });
      this.settleEntry(entry);
      onSettled();
    };

    uploader.on('progress', handleProgress);
    uploader.on('complete', handleComplete);
    uploader.on('error', handleError);

    try {
      uploader.start(fileKey);
    } catch (error) {
      cleanup();
      entry.fileKey = undefined;
      try {
        uploader.removeFile(fileKey);
      } catch {
        /* ignore */
      }
      this.patchEntry(entry, {
        status: 'error',
        errorMessage: (error as { message?: string })?.message || '未知错误',
      });
      this.settleEntry(entry);
      onSettled();
    }
  }

  /**
   * 单文件重试。
   * 与首次上传的差异：复用首次 OSS 路径与已算出的 MD5，重新取凭证后重建任务。
   */
  retryFile(uid: string) {
    const entry = this.entries.get(uid);
    if (!entry || !entry.settled) return;
    const userId = useUserStore.getState().userInfo?.userId || '';
    const uploader = this.ensureUploader(userId);

    // 清掉旧任务（restart 只支持暂停任务，失败任务必须移除后重建）
    if (entry.fileKey) {
      try {
        uploader.cancel(entry.fileKey);
        uploader.removeFile(entry.fileKey);
      } catch {
        /* ignore */
      }
      entry.fileKey = undefined;
    }

    entry.settled = false;
    entry.hasRetriedExpiredToken = false;
    this.patchEntry(entry, { status: 'pending', progress: 0, errorMessage: undefined });
    this.pending.push(uid);
    this.pumpWorkers(uploader);
  }

  /**
   * 取消单个文件（同时中止其 MD5 计算）。
   */
  cancelFile(uid: string) {
    const entry = this.entries.get(uid);
    if (!entry) return;
    entry.settled = true;
    entry.md5Controller?.abort();
    if (entry.fileKey && this.uploader) {
      try {
        this.uploader.cancel(entry.fileKey);
        this.uploader.removeFile(entry.fileKey);
      } catch {
        /* ignore */
      }
      entry.fileKey = undefined;
    }
    this.entries.delete(uid);
    this.pending = this.pending.filter((id) => id !== uid);
  }

  /** 取消整批（由编排层按 uid 列表调用） */
  cancelFiles(uids: string[]) {
    uids.forEach((uid) => this.cancelFile(uid));
  }

  /** 用户登出 / 切换账号时整体重置 */
  reset() {
    this.cancelAllActive();
    this.entries.forEach((entry) => entry.md5Controller?.abort());
    this.entries.clear();
    this.pending = [];
    this.activeWorkers = 0;
    this.uploader = null;
    this.uploaderUserId = null;
  }

  /** 内部：合并写入运行时记录并回调外层 */
  private patchEntry(entry: QueueEntry, patch: UploadFilePatch) {
    if (patch.status === 'success' || patch.status === 'error') {
      entry.settled = true;
    }
    entry.callbacks.onFilePatch(entry.uid, patch);
  }

  private settleEntry(entry: QueueEntry) {
    entry.settled = true;
    entry.callbacks.onFileSettled(entry.uid);
  }
}

const getExtSafe = (fileName: string) => (fileName.split('.').pop() || '').toLowerCase();

/** 全局单例 */
export const uploadClient = new UploadClient();
